/** 火山混音仅发给远端，本地不播放。对应 AudioMixingType.PUBLISH。 */
export const AUDIO_MIXING_TYPE_PUBLISH = 1;
/** 近乎听不见，只为让编码器持续出包。白噪音会变成明显嘶声。 */
const KEEPALIVE_AMPLITUDE = 0.0008;
const KEEPALIVE_FREQUENCY_HZ = 80;
const KEEPALIVE_SAMPLE_RATE = 48_000;
export const KEEPALIVE_BUFFER_DURATION_MS = 1_000;

export interface VolcAudioMixingManagerLike {
  disableAudioMixingBuffer: () => void;
  enableAudioMixingBuffer: (type: number) => void;
  pushAudioMixingBuffer: (buffer: AudioBuffer) => void;
}

const createKeepaliveAudioBuffer = (): AudioBuffer => {
  const OfflineContext = globalThis.OfflineAudioContext;
  if (typeof OfflineContext !== 'function') {
    throw new Error('OfflineAudioContext is unavailable');
  }
  const frameCount = Math.floor((KEEPALIVE_SAMPLE_RATE * KEEPALIVE_BUFFER_DURATION_MS) / 1_000);
  const context = new OfflineContext(1, frameCount, KEEPALIVE_SAMPLE_RATE);
  const buffer = context.createBuffer(1, frameCount, KEEPALIVE_SAMPLE_RATE);
  const samples = buffer.getChannelData(0);
  const phaseStep = (2 * Math.PI * KEEPALIVE_FREQUENCY_HZ) / KEEPALIVE_SAMPLE_RATE;
  for (let index = 0; index < samples.length; index += 1) {
    samples[index] = Math.sin(index * phaseStep) * KEEPALIVE_AMPLITUDE;
  }
  return buffer;
};

/**
 * 关麦时往已发布的音频轨混入极低幅度低频正弦，保持上行不断包。
 * 走 SDK 混音而不是自定义音源，避免再新建 AudioContext 抢走下行播放。
 */
export class MuteAudioKeepalive {
  private buffer: AudioBuffer | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private mixing: VolcAudioMixingManagerLike | null = null;

  get isActive(): boolean {
    return this.intervalId !== null;
  }

  start(mixing: VolcAudioMixingManagerLike | undefined): void {
    this.stop();
    if (!mixing) {
      return;
    }
    try {
      const buffer = this.buffer ?? createKeepaliveAudioBuffer();
      this.buffer = buffer;
      mixing.enableAudioMixingBuffer(AUDIO_MIXING_TYPE_PUBLISH);
      mixing.pushAudioMixingBuffer(buffer);
      mixing.pushAudioMixingBuffer(buffer);
      this.mixing = mixing;
      this.intervalId = setInterval(() => {
        mixing.pushAudioMixingBuffer(buffer);
      }, KEEPALIVE_BUFFER_DURATION_MS);
    } catch (error) {
      try {
        mixing.disableAudioMixingBuffer();
      } catch {
        // 启动失败时尽量关掉半开的 PCM 混音。
      }
      throw error;
    }
  }

  stop(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    const mixing = this.mixing;
    this.mixing = null;
    if (!mixing) {
      return;
    }
    try {
      mixing.disableAudioMixingBuffer();
    } catch {
      // 停混音失败不能挡住关麦或退房。
    }
  }
}
